import { AxiosService } from 'src/axios/axios.service';
import { tAxiosCustomConfig } from 'src/axios/axios.types';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';

/**
 * SERVER-side acknowledgment persistence — the seam, not yet the
 * implementation.
 *
 * Since RET-1877 acknowledgments persist LOCALLY (per browser, per
 * workspace+user) through `AlertCenter.storage.ts`; a reload keeps them, a
 * different device does not. That local layer is deliberate and complete on
 * its own terms. THIS seam is about durability beyond the browser: the
 * endpoint below does not exist yet, and the open question is not "what shape
 * should the request be" but "can the backend mint a stable id for an alert
 * that is derived on the client" (Q1).
 *
 * The contract we would consume, mirroring the one that already ships for
 * agenda notifications (`POST notifications/notifications/{id}/dismiss`,
 * `src/providers/NotificationProvider/Notifications.service.ts`):
 *
 *   POST  alerts/alerts/{alertKey}/acknowledge
 *   GET   alerts/alerts/acknowledged?workspace_id={wid}&user_id={uid}
 *
 * `alertKey` is `<alert>:<workspace>[:<period>]` — built by `buildAlertKey` in
 * `src/alerts/AlertCenter.functions.ts`. The period segment is what makes an
 * acknowledgment expire: quota-at-95% acknowledged in one billing period must
 * return in the next, which a bare `quota-near` id cannot express.
 *
 * Two constraints on whoever implements this:
 *
 * 1. Write local state only AFTER the network call resolves, and surface a
 *    failure rather than swallowing it. The agenda-notification precedent gets
 *    this wrong — its catch does `Promise.resolve(error)`, so a failed dismiss
 *    resolves and the item disappears locally anyway. Do not copy that.
 * 2. Never OR a local flag with the backend value. A guard that reads
 *    `acknowledged || locallyAcknowledged` cannot tell a real acknowledgment
 *    from a write that failed.
 *
 * Until the endpoint exists, `persistAcknowledgment` is intentionally a no-op
 * that reports "not persisted" so callers cannot mistake session state for
 * durable state.
 */

export type tAcknowledgeResult = {
  persisted: boolean;
  reason?: 'not-implemented' | 'request-failed';
};

/** The eventual call, kept unexported until the endpoint lands. */
async function postAcknowledgment(alertKey: string): Promise<void> {
  const config: tAxiosCustomConfig = { isLegacy: true, headers: undefined };
  await AxiosService.post(`alerts/alerts/${alertKey}/acknowledge`, {}, config);
}

export async function persistAcknowledgment(
  alertKey: string,
): Promise<tAcknowledgeResult> {
  // The endpoint is not built. Returning `persisted: false` keeps the caller
  // honest: acknowledgment is session-scoped until Q1 is answered.
  void alertKey;
  void postAcknowledgment;
  return { persisted: false, reason: 'not-implemented' };
}

export function reportAcknowledgmentFailure(alertKey: string, error: unknown) {
  LLog.error(
    {
      component: LOG_COMPONENTS.NOTIFICATION,
      method: 'AlertCenter.service',
      operation: LOG_OPERATIONS.ERROR,
    },
    `No se pudo guardar el acuse del aviso ${alertKey}`,
    error as Error,
  );
}

// AGI-428: workspace notices

/**
 * The envelope `GET {V2}workspace/{wid}/notices` returns, mirroring
 * `getFeatureFlags` on the same bff-web router. `data` is `[]` when the
 * workspace has no visible notice, never a 404.
 *
 * `data` is deliberately `unknown[]`: the rows carry a jsonb payload seeded
 * from a CSV, so the TRUTH about its shape is the runtime guard
 * (`parseMetaPricingNotice`), not a declared type the compiler cannot check
 * against the wire.
 */
export type tWorkspaceNoticesResponse = {
  code: number;
  success: boolean;
  data: unknown[];
};

/**
 * Same path style and same instance as the feature flags (no `isLegacy` /
 * `isBFFService` flag: the default base URL is `BASE_URL_V2`, which the
 * gateway rewrites wholesale to bff-web).
 *
 * `skipSentryCapture` for the flags' reason: until workspace-service and
 * bff-web are in production this endpoint answers 404 on every page load, and
 * the source already reports the definitive failure through `LLog`. A
 * transport-level capture per load would be noise, not signal.
 */
export async function fetchWorkspaceNotices(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<tWorkspaceNoticesResponse> {
  return AxiosService.get<tWorkspaceNoticesResponse>(
    `workspace/${workspaceId}/notices`,
    { skipSentryCapture: true, signal },
  );
}
